import { MonitoreoEstado } from "../../../../../modulos/monitoreo/sesion/semaforo";

/**
 * Paleta categórica de lotes, en **orden fijo**. Es la paleta de referencia
 * validada: sobre la superficie blanca de la app (`--sx-bg-surface`) pasa banda
 * de luminosidad, piso de croma, separación CVD (peor par adyacente ΔE 9.1) y
 * piso de visión normal (ΔE 19.6).
 *
 * Tres de estos tonos quedan por debajo de 3:1 de contraste contra el blanco,
 * lo que obliga a "alivio" por etiqueta visible: lo cubren las pills de lote,
 * que muestran el nombre junto a su punto de color de forma permanente sobre
 * el gráfico.
 *
 * Reglas que NO se pueden romper acá:
 * - **Nunca se cicla.** Un noveno lote no recibe un tono generado ni repetido.
 * - **El color sigue al lote, no a su posición en la selección.** El slot se
 *   asigna por el orden alfabético estable de TODOS los lotes de la finca, no
 *   de los activos — si no, apagar una pill repintaría a las demás.
 */
export const PALETA_CATEGORICA = [
  "#2a78d6", // azul
  "#eb6834", // naranja
  "#1baf7a", // aqua
  "#eda100", // amarillo
  "#e87ba4", // magenta
  "#008300", // verde
  "#4a3aa7", // violeta
  "#e34948", // rojo
] as const;

/** Máximo de series categóricas simultáneas (= slots de la paleta). Más allá
 *  de esto no hay tono que asignar sin ciclar. En el dashboard acota los lotes
 *  del gráfico; en el reporte, las plagas de las barras agrupadas. */
export const LOTE_MAX_SERIES = PALETA_CATEGORICA.length;

/**
 * Primer color de la paleta que no esté ocupado, o `null` si están los ocho.
 *
 * Los slots se reparten **al activar un lote**, no de antemano sobre todo el
 * catálogo. Repartirlos de antemano dejaba a los lotes que excedían la paleta
 * sin color *para siempre*: en una finca de veinte, los últimos doce no se
 * podían graficar nunca, ni siquiera apagando todos los demás.
 *
 * Un lote conserva su color mientras siga activo, así que apagar uno **no
 * repinta a los que quedan** — que es lo que la regla de "el color sigue al
 * lote, no a su posición" busca evitar. Al desactivarlo, su slot vuelve al
 * pool.
 *
 * @param enUso Colores actualmente asignados.
 */
export function siguienteColorLibre(enUso: Iterable<string>): string | null {
  const ocupados = new Set(enUso);
  return PALETA_CATEGORICA.find((color) => !ocupados.has(color)) ?? null;
}

/**
 * Colores de estado del semáforo. Son tokens **reservados**: no se reusan como
 * color de serie, y nunca viajan solos — siempre acompañados de su etiqueta
 * (`ESTADO_LABEL`). Duplican en literal `--sx-success`/`--sx-warning`/
 * `--sx-error` de `tokens.css` porque ECharts los recibe como opción de
 * JavaScript, donde una variable CSS no se resuelve.
 */
export const ESTADO_COLOR: Record<MonitoreoEstado, string> = {
  ok: "#16a34a",
  warn: "#d97706",
  alert: "#dc2626",
};

/**
 * Color de lo que **no se puede evaluar**: un benéfico, o un organismo sin
 * valores de referencia.
 *
 * Gris y no verde a propósito. Verde dice "lo revisamos y está bien"; acá lo
 * que corresponde decir es "lo revisamos y no sabemos", que para el que decide
 * si aplicar o no son dos cosas distintas.
 */
export const COLOR_SIN_REFERENCIA = "#9ca3af";
